import { classifyDateOnly } from "./dateOnly";

/**
 * Em dash, the app-wide placeholder for an absent value. Rendering an empty
 * string instead would collapse a table cell and make a missing figure look
 * like a layout bug rather than missing data.
 */
export const DASH = "\u2014";

/**
 * Card/grid cell text for an unknown column value. Objects and arrays use
 * JSON; empty values and non-primitive non-objects are the dash.
 */
export function stringifyUnknown(value: unknown): string {
  if (value == null || value === "") return DASH;
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }
  if (typeof value === "object") return JSON.stringify(value);
  return DASH;
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null) return DASH;
  return `${value.toFixed(3)}%`;
}

export function formatPrice(value: number | null | undefined): string {
  if (value == null) return DASH;
  return value.toFixed(3);
}

/** Basis points, e.g. "56.4 bp" — used for OAS. */
export function formatBasisPoints(value: number | null | undefined): string {
  if (value == null) return DASH;
  return `${value.toFixed(1)} bp`;
}

/** Plain duration figure (years), e.g. "6.12" — used for OASD. */
export function formatDuration(value: number | null | undefined): string {
  if (value == null) return DASH;
  return value.toFixed(2);
}

/** Numeric US-format `MM/DD/YYYY`, e.g. "06/02/2026". */
export function formatDate(value: string | null | undefined): string {
  if (!value) return DASH;
  const parts = classifyDateOnly(value);
  if (parts?.kind === "invalid") return DASH;
  if (parts) return `${parts.mm}/${parts.dd}/${parts.yyyy}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return DASH;
  return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

/** Numeric US-format `MM/DD`, e.g. "06/02". */
export function formatShortDate(value: string | null | undefined): string {
  if (!value) return DASH;
  const parts = classifyDateOnly(value);
  if (parts?.kind === "invalid") return DASH;
  if (parts) return `${parts.mm}/${parts.dd}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return DASH;
  return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
}

/** Numeric US-format `MM/DD/YYYY HH:mm` (24h). Alias for {@link formatDateTimeNumeric}. */
export function formatDateTime(value: string | null | undefined): string {
  return formatDateTimeNumeric(value);
}

export function formatQuantity(value: number | null | undefined): string {
  if (value == null) return DASH;
  return value.toLocaleString("en-US");
}

/**
 * A search result count. `isLowerBound` marks a total the server stopped counting at,
 * shown as a minimum: 1000000 -> "1,000,000+".
 */
export function formatResultCount(count: number, isLowerBound = false): string {
  return `${count.toLocaleString("en-US")}${isLowerBound ? "+" : ""}`;
}

/** Bond face quantity with the conventional "M" (thousands) suffix, e.g. 186 -> "186M". */
export function formatQuantityM(value: number | null | undefined): string {
  if (value == null) return DASH;
  return `${value.toLocaleString("en-US")}M`;
}

/**
 * Full-dollar issuance size (e.g. offering/deal size) with the muni convention
 * "M" = 1,000. A clean multiple of 1,000 drops the trailing thousands and shows
 * "M" (1,540,000 -> "1,540M"); anything else — "baby bonds" whose size is not a
 * round multiple — shows the full number so no precision is lost.
 */
export function formatSizeM(value: number | null | undefined): string {
  if (value == null) return DASH;
  if (Number.isInteger(value) && value % 1000 === 0) {
    return `${(value / 1000).toLocaleString("en-US")}M`;
  }
  return value.toLocaleString("en-US");
}

/** Whole-dollar USD amount, e.g. 928783000 -> "$928,783,000"; dash when absent. */
export function formatCurrencyUsd(value: number | null | undefined): string {
  if (value == null) return DASH;
  return `$${Math.round(value).toLocaleString("en-US")}`;
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** Numeric US-format `MM/DD/YYYY HH:mm` (24h) date-time, e.g. "06/02/2026 12:25". */
export function formatDateTimeNumeric(value: string | null | undefined): string {
  if (!value) return DASH;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return DASH;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
}

/** Human-readable byte size, e.g. 2048 -> "2.0 KB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
